import { useCallback, useState, type MouseEvent } from 'react';
import { ChevronDown } from 'lucide-react';
import { HtmlBody } from '@/components/HtmlBody';
import { useRecordNewsletterClick, type NewsletterIssue } from '@/lib/useNewsletter';

/**
 * This week's Unwrapped newsletter, on the home page.
 *
 * The newsletter is artwork, not text: Communications designs each issue in a
 * graphics tool and sends it as a few tall images. So there is nothing to
 * restyle inside it, and the job here is to frame it the way the rest of the
 * app is framed and then get out of its way.
 *
 * Clicks are counted on our side. One listener on the container rather than a
 * handler per link, because the body is rendered from stored markup and has no
 * React elements to attach to. The count carries a friendly name rather than a
 * bare URL: the importer sets each panel's image description to the linked
 * file's own title (for example "Smart Safe Digital Training Manual"), and that
 * description survives the API's sanitizer where a custom attribute would not.
 */

/** The name a click is recorded under: the panel's description, else its position. */
function linkLabel(anchor: HTMLAnchorElement, container: HTMLElement): string {
  const alt = anchor.querySelector('img')?.getAttribute('alt')?.trim();
  if (alt) return alt.slice(0, 120);
  const text = anchor.textContent?.trim();
  if (text) return text.slice(0, 120);
  const index = Array.from(container.querySelectorAll('a')).indexOf(anchor);
  return `Panel ${index + 1}`;
}

function weekLabel(issueDate: string): string {
  const [y, m, d] = issueDate.split('-').map(Number);
  if (!y || !m || !d) return issueDate;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export function NewsletterCard({
  issue,
  storeNo,
}: {
  issue: NewsletterIssue;
  storeNo?: number;
}) {
  const record = useRecordNewsletterClick(storeNo);
  // Collapsed on every load. Nothing is remembered between visits.
  const [open, setOpen] = useState(false);

  const onClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      // Never block the navigation on this. If the count fails, the reader
      // still gets where they were going.
      record.mutate({
        id: issue.id,
        link_url: href,
        link_label: linkLabel(anchor, e.currentTarget),
      });
    },
    [issue.id, record],
  );

  return (
    <section className="mb-6">
      <div className="overflow-hidden rounded-xl border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-candy-cream-warm dark:hover:bg-candy-dark-surface-warm"
        >
          <span className="text-xs font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
            This week&apos;s newsletter
          </span>
          <span className="flex items-center gap-2 text-xs text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Week of {weekLabel(issue.issue_date)}
            <ChevronDown
              size={16}
              aria-hidden
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </span>
        </button>

        {open && (
          /* The artwork is edge to edge. It carries its own margins and its own
             colour, so padding here would just frame it twice. */
          <div
            onClick={onClick}
            className="border-t border-candy-line [&_img]:block [&_img]:w-full [&_p]:m-0 dark:border-candy-dark-line"
          >
            <HtmlBody src={issue.body_html} />
          </div>
        )}
      </div>
    </section>
  );
}
